import React, { useState } from "react";
import { Button } from "../Button";
import { Dialog } from "../Dialog";
import Form from "../Form/Form";
import FormInputWrapper from "../Form/FormInputs/FormInputWrapper";
import useForm from "../utils/hooks/useForm";
import FilterSwitcher from "./FilterSwitcher";
import {
  ControlledFilterSwitcherWithDialogEditProps,
  FilterSwitcherWithDialogEditProps,
} from "./types";

/**
 * A filter component presented as a context switcher, with editing capability through a dialog
 */
const FilterSwitcherWithDialogEdit = ({
  editFormDefaultValues,
  editFormContent,
  onChange,
  onEdit,
  ...restProps
}: FilterSwitcherWithDialogEditProps) => {
  const { name, value, label } = restProps;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const formProps = useForm({
    defaultValues: editFormDefaultValues
      ? editFormDefaultValues
      : {
          [name]: value,
        },
  });
  const { formState } = formProps;

  const handleChange = (changeFormValues: Record<string, any>) => {
    if (onChange) {
      // only pass-through the value specific to the input within this component
      onChange(changeFormValues[name]);
    }

    formProps.reset();
    setIsEditDialogOpen(false);
  };

  const handleEditClick = () => {
    onEdit?.(value ?? "");
    setIsEditDialogOpen(true);
  };

  const editDialogActionRow = [
    <Button
      key="change-filter-value"
      variant="primary"
      type="button"
      isLoading={formState.isSubmitting}
      disabled={formState.isSubmitting}
      onClick={formProps.handleSubmit(handleChange)}
    >
      Change {label}
    </Button>,
  ];

  return (
    <>
      <FilterSwitcher
        {...restProps}
        onEdit={handleEditClick}
      />
      <Dialog
        title={label}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        actionRow={editDialogActionRow}
      >
        <Form
          {...formProps}
          onSubmit={handleChange}
        >
          {editFormContent}
        </Form>
      </Dialog>
    </>
  );
};

const ControlledFilterSwitcherWithDialogEdit = (
  props: ControlledFilterSwitcherWithDialogEditProps
) => (
  <FormInputWrapper
    {...props}
    isLabelShown={false}
    as={FilterSwitcherWithDialogEdit}
  />
);

export default FilterSwitcherWithDialogEdit;
export { ControlledFilterSwitcherWithDialogEdit };
